import React from 'react';
import MyEditor from '../RichEditor/richEditor';
import * as AdaptiveCards from "adaptivecards";
import { Button, Field, Input, Text, Textarea, Tooltip } from '@fluentui/react-components';
import { AddCircle24Regular, Attach20Regular, Info24Regular, DeleteRegular, ImageCircleRegular } from '@fluentui/react-icons';
import FileUploader from './FileUploader/fileUploader';
import { markDownToHtml } from '../../helpers/formattingHelper'
import './newsTemplate.scss'
import { NewsTemplateStructure, SectionTemplate } from './newsTemplateStructure';
import LinkSet, { IAction } from './LinkSet/linkSet';
import { enableMultiSpaces, setActions } from '../AdaptiveCard/adaptiveCard';

export interface INewsletter {
    sections: Array<INewsSection>,
    header?: string,
    card: any,
    actions: IAction[]
}


export interface INewsSection {
    title?: string,
    summary?: string,
    imageUrl?: string,
    titleErrorMessage?: string,
    summaryErrorMessage?: string,
}

interface INewsTemplateProps {
    value?: INewsletter;
    onChange: (value: INewsletter) => void;
}

interface INewsTemplateState extends INewsletter {

}

export const prepareNewsTemplateCard = (value: INewsletter) => {
    const blocks = value.sections.map(section => {
        const sectionTemplate = SectionTemplate();
        sectionTemplate.items[0].text = section.title;
        sectionTemplate.items[1].url = section.imageUrl;
        sectionTemplate.items[2].text = section.summary;
        return sectionTemplate;
    })
    const card = { ...value.card || NewsTemplateStructure }
    card.body[1].items = blocks;

    card.body[0].items[2].text = value.header;

    let urlsAndEvents: IAction[] = value.actions ? [...value.actions] : [];
    card.actions = urlsAndEvents.map(x => ({
        "type": "Action.OpenUrl",
        "title": x.title === "" ? "Button" : x.title,
        "url": x.link
    }));

    return card;
}

class NewsTemplate extends React.Component<INewsTemplateProps, INewsTemplateState> {
    private titleMaxLength: number = 200;
    private summaryMaxLength: number = 2000;
    private sectionsLimit: number = 5;

    constructor(props: INewsTemplateProps) {
        super(props);
        this.state = {
            card: NewsTemplateStructure,
            actions: props.value?.actions || [],
            sections: props.value?.sections || [],
            header: props.value?.header || "",
        };
        this.updateCard();

    }

    componentDidMount(): void {
        this.updateCard();
    }

    componentDidUpdate(prevProps: Readonly<INewsTemplateProps>, prevState: Readonly<INewsTemplateState>): void {
        if (prevState != this.state) {
            this.props.onChange(this.state);
        }

        if (prevState.sections != this.state.sections || prevState.actions != this.state.actions || prevState.header != this.state.header) {
            this.updateCard();
        }
    }

    private updateCard = () => {
        var card = prepareNewsTemplateCard(this.state);

        this.setState({ card: card }, () => {
            this.renderCard();
        });
    }

    private renderCard = () => {
        const adaptiveCard = new AdaptiveCards.AdaptiveCard();
        AdaptiveCards.AdaptiveCard.onProcessMarkdown = function (text, result) {
            result.outputHtml = markDownToHtml(text);
            result.didProcess = true;
        };
        adaptiveCard.parse(this.state.card);
        const renderedCard = adaptiveCard.render() as HTMLElement;
        const adaptiveCardContainer = document.getElementsByClassName('adaptiveCardContainer-news')[0];

        if (adaptiveCardContainer !== null && adaptiveCardContainer !== undefined) {
            const container = adaptiveCardContainer.firstChild;
            if (container != null) {
                container.replaceWith(renderedCard);
            } else {
                adaptiveCardContainer.appendChild(renderedCard);
            }
        }
    }

    private updatePropertyAt<T>(index: number, selector: (section: INewsSection) => T): void {
        var sections = [...this.state.sections];
        selector(sections[index]);
        this.setState({ sections: sections });
    }

    render() {
        return (
            <div className="news-template">
                <div className="news-template__section-cta">
                    <Field required={true} label="Header">
                        <Textarea placeholder='Header' value={this.state.header} onChange={(_, data) => {
                            const header = enableMultiSpaces(data.value);
                            this.setState({ header: header });
                        }} />
                    </Field>
                </div>
                {this.state.sections.map((section: INewsSection, index: number) =>
                    <div key={index} className="news-template__section">
                        <div className='news-template__inline-block'>
                            <Text weight="semibold" size={500}>
                                Section #{index + 1}
                            </Text>
                            <Button appearance="subtle" icon={<DeleteRegular />} onClick={() => {
                                var sections = [...this.state.sections];
                                sections = sections.filter((_, i) => i !== index);
                                this.setState({ sections: sections });
                            }} />
                        </div>
                        <Field required={true}
                            label="Title"
                            validationState={this.state.sections[index].titleErrorMessage ? 'error' : 'none'}
                            validationMessage={this.state.sections[index].titleErrorMessage}>
                            <Input value={section.title} onChange={(_, data) => {
                                this.updatePropertyAt(index, (section: INewsSection) => {
                                    section.title = data.value;
                                    section.titleErrorMessage = data.value.length > this.titleMaxLength ? "Title text is too long" : undefined;
                                    return section.title;
                                })
                            }} placeholder="Title" />
                        </Field>

                        <Field label="Image URL">
                            <div className='news-template__inline-block'>
                                <Input
                                    value={section.imageUrl || ""}
                                    contentBefore={<ImageCircleRegular />}
                                    type="url"
                                    onChange={(_, data) => {
                                        this.updatePropertyAt(index, (section: INewsSection) => {
                                            section.imageUrl = data.value;
                                            return section.imageUrl;
                                        })
                                    }}
                                    id={"section.imageUrl" + index}
                                    placeholder='Image URL' />
                                <FileUploader
                                    buttonIcon={<Attach20Regular />}
                                    buttonText={"Attach image"}
                                    url="/content/uploadImage"
                                    onSuccess={(response) => {
                                        this.updatePropertyAt.bind(this)(index, (sectionToUpdate: INewsSection) => {
                                            sectionToUpdate.imageUrl = response;
                                            return sectionToUpdate.imageUrl;
                                        })
                                    }}
                                    acceptedFileTypes=".png,.jpg,.jpeg"
                                />
                                <Tooltip
                                    content="Images must be at most 1024×1024 px, less than 1 MB size, and in PNG, JPEG, or GIF format. Animated GIF is not supported."
                                    relationship='label'>
                                    <Info24Regular
                                        className="succeeded"
                                    />
                                </Tooltip>
                            </div>
                        </Field>

                        <Field label="Summary"
                            validationState={this.state.sections[index].summaryErrorMessage ? 'error' : 'none'}
                            validationMessage={this.state.sections[index].summaryErrorMessage}
                            className='rich-editor-field'>
                            <MyEditor
                                initialValue={section.summary}
                                propertyType='Summary'
                                onPropertyChanged={(value: string) => {
                                    this.updatePropertyAt(index, (section: INewsSection) => {
                                        section.summary = value;
                                        section.summaryErrorMessage = value.length > this.summaryMaxLength ? "Summary text is too long" : undefined;
                                        return section.summary;
                                    })
                                }}
                                draftIsMount={true} />
                        </Field>

                    </div>
                )}
                <div className="news-template__section-cta news-template__inline-block">
                    <Button
                        disabled={this.state.sections?.length >= this.sectionsLimit}
                        icon={<AddCircle24Regular />}
                        onClick={() => {
                            this.setState((previous) => {
                                return {
                                    sections: [...previous.sections, { title: "", imageUrl: "", summary: "" }],
                                }
                            })
                        }}>
                        Add section
                    </Button>
                    <Tooltip
                        content="Add up to 5 sections"
                        relationship='label'>
                        <Info24Regular
                            className="succeeded"
                        />
                    </Tooltip>
                </div>
                <div className="news-template__section-cta">
                    <LinkSet value={this.state.actions} onChange={(actions: IAction[]) => this.setState({ actions: actions })} />
                </div>

            </div >
        )
    }
}

export default NewsTemplate;